import { useContext, useEffect } from "react";
import { IoArrowBackOutline } from "react-icons/io5";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import CartCard from "../components/CartCard";
import Loading from "../components/Loading";
import CartContext from "../context/CartContext";
import { fetchCartItems } from "../redux/features/cartSlice";

const CartPage = () => {
  const dispatch = useDispatch();
  const carts = useSelector((state) => state.cart.carts || []);
  const isLoading = useSelector((state) => state.cart.isLoading);
  const user_id = useSelector((state) => state.login.me?.id);
  const user = useSelector((state) => state.login.me);
  const setting = useSelector((state) => state.setting.settings);

  const { setCartSummary } = useContext(CartContext);

  useEffect(() => {
    if (user_id) {
      dispatch(fetchCartItems({ user_id }));
    }
  }, [dispatch, user_id]);

  // Calculate total price for items with and without variations
  const totalPrice = carts?.reduce((total, item) => {
    // Determine the price based on whether `product` or `variation` exists
    const price = item.product
      ? Number(item.product.selling_price) // Use product price if product exists
      : item.variation
      ? Number(item.variation.selling_price) // Use variation price if variation exists
      : 0; // Default to 0 if neither exists

    // Ensure quantity is a valid number, default to 1 if missing or invalid
    const quantity = item.quantity ? Number(item.quantity) : 1;

    // Add to the total
    return total + price * quantity;
  }, 0);

  const deliveryCharge = setting.delivery_charges || 0;
  const grandTotal = totalPrice + deliveryCharge;

  useEffect(() => {
    setCartSummary({ totalPrice, deliveryCharge, grandTotal });
  }, [totalPrice, deliveryCharge, grandTotal, setCartSummary]);

  if (!user) {
    return (
      <div className="container py-16 mx-auto md:w-[30%] px-4 md:px-0">
        <p className="mb-5 text-2xl font-medium text-center">
          You are not logged in. Please log in to view your cart.
        </p>
        <Link className="flex justify-center" to="/register">
          <button className="flex items-center justify-center gap-1 p-3 px-5 text-white uppercase duration-300 bg-black border border-black rounded-md hover:bg-white hover:text-black">
            Register Here
            <IoArrowBackOutline />
          </button>
        </Link>
      </div>
    );
  }

  // Check if all items are in stock
  const allInStock = !carts?.find((cart) => {
    if (cart.variation) {
      return cart.variation.stock === 0;
    }
    return false; // Assume items without variations are in stock
  })?.id;

  return (
    <div className="container pb-20">
      <h1 className="mb-6 text-3xl font-semibold text-white">Your Cart</h1>
      {user && (
        <div className="grid grid-cols-1 gap-8 px-4 lg:grid-cols-12">
          <div className="col-span-full md:col-span-8">
            {isLoading ? (
              <div className="flex justify-center h-[calc(100vh-4rem)] items-center">
                <Loading />
              </div>
            ) : (
              <>
                {carts?.length === 0 ? (
                  <div className="">
                    <p className="mb-5 text-2xl font-medium">
                      Your cart is empty.
                    </p>
                    <Link to="/">
                      <button className="flex items-center gap-1 p-3 px-5 text-white uppercase duration-300 bg-black border border-black hover:bg-white hover:text-black">
                        <IoArrowBackOutline /> Go back to shopping
                      </button>
                    </Link>
                  </div>
                ) : (
                  <ul className="gap-6 space-y-5">
                    <div className="flex flex-col gap-5">
                      {carts?.map((item, index) => (
                        <CartCard key={index} item={item} />
                      ))}
                    </div>
                  </ul>
                )}
              </>
            )}
          </div>

          {carts?.length > 0 && (
            <div className="col-span-full sticky top-32 md:col-span-4 border h-[17rem] md:w-[60%] lg:w-full bg-white opacity-95 border-gray-200  rounded-3xl p-8">
              <h2 className="text-xl font-semibold text-center">
                ORDER SUMMARY
              </h2>
              <div>
                <div className="flex items-center justify-between">
                  <p className="py-3">Price</p>
                  <p>Rs. {totalPrice}</p>
                </div>
                <div className="flex items-center justify-between">
                  <p>Delivery Charge</p>
                  <p>Rs. {deliveryCharge}</p>
                </div>
                <hr className="mt-2 border border-gray-300" />
                <div className="flex items-center justify-between">
                  <p className="pt-3 font-semibold">Total</p>
                  <p className="font-semibold">Rs. {grandTotal}</p>
                </div>
              </div>
              <Link to="/checkout">
                <button
                  disabled={!allInStock}
                  className={`cart-checkout-btn mt-4 ${
                    !allInStock ? "bg-gray-500" : "bg-black"
                  } text-white py-3 px-5 w-full rounded-md`}
                >
                  Proceed to Checkout
                </button>
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CartPage;
